/* Triumph DATA PLATE | tl-product.js
   Product page behaviour (sections/main-product, sections/product-recommendations). Vanilla ES2017.
     [data-tl-pdp]   gallery (scroll-snap track, thumbs, arrows, keyboard, zoom <dialog>), variant picker
                     (embedded variant JSON → hidden id, price + MSRP, SKU, availability, ?variant=, featured
                     media), quantity stepper, "Before you order" plates (closed on phones, as on Wix), and
                     the mobile sticky add-to-cart bar (Wix 03: shows once the main Add to cart button has
                     scrolled above the viewport, at 768px and below; hidden while a drawer locks scrolling,
                     while the chosen variant cannot be bought, and for 4s after it is pressed).
     [data-tl-recs]  related products: fetched from the Section Rendering endpoint when near the viewport.
   Add to cart itself is tl-global.js (form[data-tl-product-form] → TL.cart.add, 'tl:cart:added').
   Emits 'tl:variant:change' {variant, sectionId} on the section root (bubbles) after a variant change.
   Theme editor: re-inits on shopify:section:load, tidies up on unload, block:select opens the plates. */
(function () {
  'use strict';

  var TL = window.TL = window.TL || {};
  var html = document.documentElement;

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function mm(query) { return !!(window.matchMedia && window.matchMedia(query).matches); }
  function reduced() { return TL.prefersReducedMotion ? TL.prefersReducedMotion() : mm('(prefers-reduced-motion: reduce)'); }
  function str(key, fallback) { return (TL.strings && TL.strings[key]) || fallback; }
  function money(cents) {
    if (TL.formatMoney) return TL.formatMoney(cents);
    return '$' + (Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function raf(fn) { return window.requestAnimationFrame ? window.requestAnimationFrame(fn) : setTimeout(fn, 16); }
  function emit(el, name, detail) {
    var ev;
    try { ev = new CustomEvent(name, { bubbles: true, detail: detail }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent(name, true, false, detail); }
    el.dispatchEvent(ev);
  }

  /* section init helper: TL.onSections from tl-global.js, or a local equivalent */
  function onSections(selector, init, destroy) {
    if (typeof TL.onSections === 'function') { TL.onSections(selector, init, destroy); return; }
    function run(scope) {
      var els = [];
      if (scope.matches && scope.matches(selector)) els.push(scope);
      els = els.concat(qsa(selector, scope));
      els.forEach(function (el) {
        if (el.dataset.tlInit) return;
        el.dataset.tlInit = '1';
        try { init(el); } catch (err) { if (window.console) console.error(err); }
      });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { run(document); });
    else run(document);
    document.addEventListener('shopify:section:load', function (e) { run(e.target); });
    document.addEventListener('shopify:section:unload', function (e) {
      qsa(selector, e.target).forEach(function (el) {
        if (destroy) { try { destroy(el); } catch (err) { if (window.console) console.error(err); } }
        delete el.dataset.tlInit;
      });
    });
  }

  /* move focus + attention to a block (Wix 03 goTo) */
  function attention(box, focusEl) {
    if (!box) return;
    try { box.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' }); } catch (e) { box.scrollIntoView(); }
    box.classList.add('tl-attn');
    setTimeout(function () { box.classList.remove('tl-attn'); }, 2400);
    var target = focusEl || qs('select, input:not([type="hidden"]), button:not([disabled])', box);
    if (target && target.focus) { try { target.focus({ preventScroll: true }); } catch (e2) { target.focus(); } }
  }

  /* ── gallery ──────────────────────────────────────────────────────── */
  function Gallery(root) {
    var track = qs('[data-tl-gal-track]', root);
    var slides = track ? qsa('.tl-pdp-gal__slide', track) : [];
    var thumbRow = qs('.tl-pdp-gal__thumbs', root);
    var thumbs = qsa('[data-tl-gal-thumb]', root);
    var prev = qs('[data-tl-gal-prev]', root);
    var next = qs('[data-tl-gal-next]', root);
    var counter = qs('[data-tl-gal-index]', root);
    var dialog = qs('[data-tl-zoom]', root);
    var index = 0, pending = 0, opener = null, locked = false;
    var cleanups = [];

    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts || false);
      cleanups.push(function () { el.removeEventListener(type, fn, opts || false); });
    }

    function pauseOthers(active) {
      slides.forEach(function (s, k) {
        if (k === active) return;
        qsa('video', s).forEach(function (v) { try { v.pause(); } catch (e) { /* ignore */ } });
      });
    }

    function revealThumb(t) {
      if (!thumbRow || !t) return;
      var li = t.parentNode;
      var left = li.offsetLeft - thumbRow.offsetLeft;
      if (left < thumbRow.scrollLeft) thumbRow.scrollLeft = left - 4;
      else if (left + li.offsetWidth > thumbRow.scrollLeft + thumbRow.clientWidth) thumbRow.scrollLeft = left + li.offsetWidth - thumbRow.clientWidth + 4;
    }

    function setActive(i) {
      index = i;
      slides.forEach(function (s, k) {
        s.classList.toggle('is-active', k === i);
        /* only the visible slide's zoom button is in the tab order */
        qsa('[data-tl-zoom-open]', s).forEach(function (b) { b.tabIndex = k === i ? 0 : -1; });
      });
      thumbs.forEach(function (t) {
        if (parseInt(t.getAttribute('data-index'), 10) === i) { t.setAttribute('aria-current', 'true'); revealThumb(t); }
        else t.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(i + 1);
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= slides.length - 1;
      pauseOthers(i);
    }

    function goTo(i, smooth) {
      if (!track || !slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      var left = slides[i].offsetLeft - slides[0].offsetLeft;
      if (typeof track.scrollTo === 'function') track.scrollTo({ left: left, behavior: smooth && !reduced() ? 'smooth' : 'auto' });
      else track.scrollLeft = left;
      setActive(i);
    }

    function showMedia(id, smooth) {
      if (!id) return;
      for (var k = 0; k < slides.length; k++) {
        if (String(slides[k].getAttribute('data-media-id')) === String(id)) { goTo(k, smooth !== false); return; }
      }
    }

    function onScroll() {
      if (pending) return;
      pending = raf(function () {
        pending = 0;
        var w = track.clientWidth || 1;
        var i = Math.round(track.scrollLeft / w);
        if (i !== index && i >= 0 && i < slides.length) setActive(i);
      });
    }

    function onKey(e) {
      var map = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: slides.length - 1 };
      if (!(e.key in map) || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      goTo(map[e.key], true);
    }

    function openZoom(btn) {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      opener = btn;
      if (!dialog.open) dialog.showModal();
      if (!locked && TL.lockScroll) { TL.lockScroll(true); locked = true; }
      var item = qs('[data-media-id="' + btn.getAttribute('data-tl-zoom-open') + '"]', dialog);
      var list = qs('.tl-pdp-zoom__list', dialog);
      if (item && list) raf(function () { list.scrollTop = item.offsetTop - list.offsetTop - 18; });
    }

    function onClick(e) {
      var t = e.target;
      var thumb = t.closest('[data-tl-gal-thumb]');
      if (thumb && root.contains(thumb)) { goTo(parseInt(thumb.getAttribute('data-index'), 10) || 0, true); return; }
      if (t.closest('[data-tl-gal-prev]')) { goTo(index - 1, true); return; }
      if (t.closest('[data-tl-gal-next]')) { goTo(index + 1, true); return; }
      var z = t.closest('[data-tl-zoom-open]');
      if (z) { openZoom(z); return; }
      if (t.closest('[data-tl-zoom-close]') && dialog && dialog.open) dialog.close();
    }

    function onZoomClose() {
      if (locked && TL.lockScroll) { TL.lockScroll(false); locked = false; }
      if (opener && opener.focus) { try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); } }
    }

    function onResize() { if (track && slides.length) goTo(index, false); }

    on(root, 'click', onClick);
    if (track && slides.length > 1) {
      on(track, 'scroll', onScroll, { passive: true });
      on(track, 'keydown', onKey);
      on(window, 'resize', TL.debounce ? TL.debounce(onResize, 150) : onResize);
    }
    on(dialog, 'close', onZoomClose);
    if (slides.length) setActive(0);

    return {
      showMedia: showMedia,
      destroy: function () {
        cleanups.forEach(function (fn) { fn(); });
        if (dialog && dialog.open) dialog.close();
        if (locked && TL.lockScroll) { TL.lockScroll(false); locked = false; }
      }
    };
  }

  /* ── product (variants, price, quantity, plates, sticky bar) ──────── */
  function initProduct(root) {
    var cleanups = [];
    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts || false);
      cleanups.push(function () { el.removeEventListener(type, fn, opts || false); });
    }

    var sectionId = root.getAttribute('data-section-id');
    var variants = [];
    var json = qs('[data-tl-pdp-variants]', root);
    try { variants = JSON.parse(json ? json.textContent : '[]') || []; } catch (e) { variants = []; }
    var form = document.getElementById(root.getAttribute('data-form-id'));
    var idInput = form ? form.querySelector('[data-tl-pdp-id]') : null;
    var atc = qs('[data-tl-pdp-atc]', root);
    var opts = qs('[data-tl-pdp-opts]', root);
    var showMsrp = root.getAttribute('data-show-msrp') === 'true';
    var hasVariants = root.getAttribute('data-has-variants') === 'true';
    var galEl = qs('[data-tl-gal]', root);
    var gallery = galEl ? Gallery(galEl) : null;
    var current = null;

    function byId(id) {
      for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(id)) return variants[i];
      return null;
    }
    current = byId(idInput ? idInput.value : null) || variants[0] || null;

    /* option controls */
    function controls() { return opts ? qsa('[data-tl-opt]', opts) : []; }
    function selected() {
      var sel = [];
      controls().forEach(function (el) {
        var p = parseInt(el.getAttribute('data-position'), 10) - 1;
        if (el.tagName === 'SELECT') sel[p] = el.value;
        else if (el.checked) sel[p] = el.value;
      });
      return sel;
    }
    function setControls(options) {
      controls().forEach(function (el) {
        var p = parseInt(el.getAttribute('data-position'), 10) - 1;
        if (el.tagName === 'SELECT') { if (el.value !== options[p]) el.value = options[p]; }
        else el.checked = el.value === options[p];
      });
    }
    function matches(v, sel) {
      var o = v.options || [];
      for (var i = 0; i < o.length; i++) if (sel[i] !== undefined && o[i] !== sel[i]) return false;
      return o.length > 0;
    }
    function exact(sel) {
      for (var i = 0; i < variants.length; i++) if (matches(variants[i], sel)) return variants[i];
      return null;
    }
    /* no exact match: keep the value just chosen and as many of the others as possible, available first */
    function closest(sel, pos) {
      var best = null, bestScore = -1;
      variants.forEach(function (v) {
        var o = v.options || [];
        if (pos >= 0 && o[pos] !== sel[pos]) return;
        var score = 0;
        for (var i = 0; i < o.length; i++) if (o[i] === sel[i]) score += 2;
        if (v.available) score += 1;
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best;
    }

    function markAvailability(sel) {
      if (!opts) return;
      qsa('.tl-pdp-opts__opt', opts).forEach(function (group) {
        var p = parseInt(group.getAttribute('data-position'), 10) - 1;
        qsa('[data-tl-opt], option', group).forEach(function (el) {
          if (el.tagName === 'SELECT') return;
          var value = el.value, exists = false, avail = false;
          variants.forEach(function (v) {
            var o = v.options || [];
            if (o[p] !== value) return;
            for (var q = 0; q < o.length; q++) if (q !== p && sel[q] !== undefined && o[q] !== sel[q]) return;
            exists = true;
            if (v.available) avail = true;
          });
          var note = !exists ? str('unavailable', 'Unavailable') : (!avail ? str('soldOut', 'Sold out') : '');
          if (el.tagName === 'OPTION') {
            var base = el.getAttribute('data-label') || el.value;
            el.textContent = note ? base + ' – ' + note : base;
          } else {
            var label = el.nextElementSibling;
            if (!label) return;
            label.classList.toggle('is-unavailable', !avail);
            var sr = qs('[data-tl-opt-note]', label);
            if (sr) sr.textContent = note ? ', ' + note : '';
          }
        });
        var cur = qs('[data-tl-opt-current]', group);
        if (cur && sel[p] !== undefined) cur.textContent = sel[p];
      });
    }

    function setLabel(text) {
      var label = atc ? qs('[data-tl-atc-label]', atc) : null;
      if (!label) return;
      label.removeAttribute('data-tl-label');
      label.textContent = text;
    }

    function render(v) {
      var priceBox = qs('.tl-pdp__price [data-tl-price]', root);
      var stickyPrice = qs('[data-tl-pdp-sticky-price]', root);
      if (v) {
        if (idInput) idInput.value = v.id;
        if (priceBox) {
          var curEl = qs('[data-tl-price-current]', priceBox);
          if (curEl) curEl.innerHTML = money(v.price);
          var msrp = qs('[data-tl-price-msrp]', priceBox);
          if (msrp) {
            var showIt = showMsrp && v.compare_at_price && v.compare_at_price > v.price;
            msrp.hidden = !showIt;
            var cmp = qs('[data-tl-price-compare]', msrp);
            if (cmp) cmp.innerHTML = showIt ? money(v.compare_at_price) : '';
          }
        }
        if (stickyPrice) stickyPrice.innerHTML = money(v.price);
        var skuWrap = qs('[data-tl-pdp-sku-wrap]', root);
        if (skuWrap) {
          var skuEl = qs('[data-tl-pdp-sku]', skuWrap);
          if (skuEl) skuEl.textContent = v.sku || '';
          skuWrap.hidden = !v.sku;
        }
        if (atc) {
          atc.disabled = !v.available;
          setLabel(v.available ? str('addToCart', 'Add to cart') : str('soldOut', 'Sold out'));
        }
        var rule = v.quantity_rule;
        var qty = qs('[data-tl-qty-input]', root);
        if (qty && rule) {
          qty.min = rule.min || 1;
          qty.step = rule.increment || 1;
          if (rule.max) qty.max = rule.max; else qty.removeAttribute('max');
          clampQty(qty);
        }
        if (gallery && v.featured_media && v.featured_media.id) gallery.showMedia(v.featured_media.id, true);
        if (hasVariants && window.history && window.history.replaceState && window.URL) {
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', v.id);
            window.history.replaceState(window.history.state, '', u.toString());
          } catch (e) { /* ignore */ }
        }
      } else if (atc) {
        atc.disabled = true;
        setLabel(str('unavailable', 'Unavailable'));
      }
      current = v;
      emit(root, 'tl:variant:change', { variant: v, sectionId: sectionId });
      if (sticky) sticky.apply();
    }

    function onOptionChange(e) {
      var el = e.target.closest ? e.target.closest('[data-tl-opt]') : null;
      if (!el || !opts || !opts.contains(el)) return;
      var pos = parseInt(el.getAttribute('data-position'), 10) - 1;
      var sel = selected();
      var v = exact(sel);
      if (!v) {
        v = closest(sel, pos);
        if (v) { setControls(v.options); sel = v.options.slice(); }
      }
      markAvailability(sel);
      render(v);
    }

    /* quantity */
    function clampQty(input) {
      var min = parseInt(input.min, 10) || 1;
      var max = parseInt(input.max, 10) || Infinity;
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < min) v = min;
      if (v > max) v = max;
      if (String(v) !== input.value) input.value = v;
      qsa('[data-tl-qty]', root).forEach(function (b) {
        var dir = parseInt(b.getAttribute('data-tl-qty'), 10);
        b.disabled = dir < 0 ? v <= min : v >= max;
      });
    }
    function onQtyClick(e) {
      var b = e.target.closest ? e.target.closest('[data-tl-qty]') : null;
      if (!b || !root.contains(b)) return;
      var input = qs('[data-tl-qty-input]', root);
      if (!input) return;
      var step = parseInt(input.step, 10) || 1;
      input.value = (parseInt(input.value, 10) || 0) + step * parseInt(b.getAttribute('data-tl-qty'), 10);
      clampQty(input);
      emit(input, 'change', {});
    }

    /* "Before you order" plates: closed on phones (Wix 02), open elsewhere */
    qsa('[data-tl-pdp-info][data-collapse-mobile]', root).forEach(function (info) {
      if (mm('(max-width: 640px)')) qsa('details', info).forEach(function (d) { d.open = false; });
    });

    /* sticky add-to-cart bar (Wix 03) */
    var sticky = (function () {
      var bar = qs('[data-tl-pdp-sticky]', root);
      if (!bar || !atc) return null;
      var btn = qs('[data-tl-pdp-sticky-btn]', bar);
      var past = false, hold = 0, io = null, mo = null, timer = 0;
      function off() { return !atc || atc.disabled || atc.getAttribute('aria-disabled') === 'true'; }
      function apply() {
        var on = atc.isConnected !== false && past && Date.now() > hold && !html.classList.contains('tl-scroll-lock') && mm('(max-width: 768px)') && !off();
        if (bar.classList.contains('is-on') === on) return;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (btn) btn.tabIndex = on ? 0 : -1;
        html.classList.toggle('tl-pdp-sticky-on', on);
      }
      function measure() { past = atc.getBoundingClientRect().bottom <= 0; apply(); }
      var queued = 0;
      function onScrollOrResize() { if (queued) return; queued = raf(function () { queued = 0; measure(); }); }
      function showError(message) {
        var box = form ? form.querySelector('[data-tl-form-error]') : null;
        if (box) { box.textContent = message; box.hidden = !message; }
      }
      function press() {
        if (Date.now() < hold) return;
        if (off()) { attention(opts || qs('.tl-pdp-buy', root), null); return; }
        if (!form) return;
        hold = Date.now() + 4000;
        apply();
        clearTimeout(timer);
        timer = setTimeout(apply, 4100);
        if (!TL.cart || typeof TL.cart.add !== 'function' || typeof window.fetch !== 'function') {
          if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
          return;
        }
        if (form.getAttribute('data-tl-busy') === 'true') return;
        form.setAttribute('data-tl-busy', 'true');
        showError('');
        TL.cart.add(form).catch(function (err) {
          hold = 0;
          showError((err && err.message) || str('error', 'This item could not be added to the cart. Please try again.'));
          attention(qs('.tl-pdp-buy', root), atc);
          apply();
        }).then(function () { form.removeAttribute('data-tl-busy'); });
      }
      if (window.IntersectionObserver) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.target === atc) past = !en.isIntersecting && en.boundingClientRect.bottom <= 0; });
          apply();
        });
        io.observe(atc);
      }
      on(window, 'scroll', onScrollOrResize, { passive: true });
      on(window, 'resize', onScrollOrResize);
      on(btn, 'click', press);
      if (window.MutationObserver) {
        mo = new MutationObserver(apply);
        mo.observe(html, { attributes: true, attributeFilter: ['class'] });
      }
      measure();
      return {
        apply: apply,
        destroy: function () {
          if (io) io.disconnect();
          if (mo) mo.disconnect();
          clearTimeout(timer);
          html.classList.remove('tl-pdp-sticky-on');
        }
      };
    })();

    on(root, 'change', onOptionChange);
    on(root, 'click', onQtyClick);
    var qtyInput = qs('[data-tl-qty-input]', root);
    if (qtyInput) {
      on(qtyInput, 'change', function () { clampQty(qtyInput); });
      clampQty(qtyInput);
    }

    /* initial sync (a restored form state may differ from the rendered variant) */
    if (opts && variants.length) {
      var sel0 = selected();
      var v0 = exact(sel0);
      if (v0 && current && String(v0.id) !== String(current.id)) render(v0);
      markAvailability(v0 ? v0.options : (current ? current.options : sel0));
    }

    root.__tlPdp = {
      destroy: function () {
        cleanups.forEach(function (fn) { fn(); });
        if (gallery) gallery.destroy();
        if (sticky) sticky.destroy();
      },
      variant: function () { return current; }
    };
  }

  function destroyProduct(root) {
    if (root.__tlPdp) { root.__tlPdp.destroy(); root.__tlPdp = null; }
  }

  /* ── related products ─────────────────────────────────────────────── */
  function initRecs(el) {
    var url = el.getAttribute('data-url');
    if (!url || el.querySelector('.tl-card')) return;
    var io = null, done = false;
    function load() {
      if (done) return;
      done = true;
      fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      }).then(function (text) {
        var tmp = document.createElement('div');
        tmp.innerHTML = text;
        var fresh = tmp.querySelector('[data-tl-recs]');
        if (fresh && fresh.querySelector('.tl-card')) {
          el.innerHTML = fresh.innerHTML;
          el.classList.remove('tl-pdp-recs--empty');
          el.hidden = false;
        } else {
          el.classList.add('tl-pdp-recs--empty');
        }
      }).catch(function () {
        el.classList.add('tl-pdp-recs--empty');
      });
    }
    /* a jump past the shell (End key, anchor link) never makes it intersect: check its position on scroll too */
    var queued = 0;
    function check() {
      queued = 0;
      if (done) return;
      if (el.getBoundingClientRect().top < (window.innerHeight || 800) + 400) { stop(); load(); }
    }
    function onScroll() { if (!queued) queued = raf(check); }
    function stop() {
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScroll);
    }
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(function (entries) {
        if (entries.some(function (en) { return en.isIntersecting; })) { stop(); load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(el);
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      load();
    }
    el.__tlRecs = { destroy: stop };
  }

  function destroyRecs(el) {
    if (el.__tlRecs) { el.__tlRecs.destroy(); el.__tlRecs = null; }
  }

  onSections('[data-tl-pdp]', initProduct, destroyProduct);
  onSections('[data-tl-recs]', initRecs, destroyRecs);

  /* theme editor: selecting the "Before you order" block opens its plates */
  document.addEventListener('shopify:block:select', function (e) {
    var info = e.target && e.target.closest ? e.target.closest('[data-tl-pdp-info]') : null;
    if (info) qsa('details', info).forEach(function (d) { d.open = true; });
  });
})();
